/**
 * Wildlife conservation drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawing_topics`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const wildlifeConservationDrawingsPack: ToilePack = {
    slug: 'wildlife-conservation-drawings',
    category: 'coloring',
    name: 'Wildlife conservation drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'tiger', label: 'Tiger', subject: 'a tiger walking through tall grass with a stream in front'},
        {key: 'birds', label: 'Birds nest', subject: 'a nest with three eggs on a branch and a mother bird'},
        {key: 'elephant', label: 'Elephant family', subject: 'an elephant mother and calf walking under trees'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil sketch', style: 'a soft graphite pencil sketch with clear outlines and light hatching for shade', palette: 'Gray pencil on white paper, no color.', framing: 'Drawn on a plain white paper sheet, centered with a white margin.'},
        {key: 'crayon', label: 'Colour pencil', style: 'a colour pencil and crayon drawing, simple outlines filled with bright even strokes of colour', palette: 'Bright crayon colours on white paper.', framing: 'Drawn on a plain white drawing-book page, centered with a white margin.'},
        {key: 'outline', label: 'Outline to colour', style: 'a bold black outline drawing with flat marker colour filling each shape', palette: 'Black outlines with flat bright colour fills on white paper.', framing: 'Flat 2D drawing isolated on a pure white background with a white margin, clean closed black line work.'},
    ],
    directions: ['a rhino', 'a peacock', 'a waterhole'],
    directionHint: 'try: a rhino',
    seo: {
        title: 'Wildlife Conservation Drawings: Tiger, Birds, Elephants',
        metaDescription: 'Nine wildlife conservation drawings: a tiger in the forest, birds in a nest and an elephant family, in pencil, colour and outline.',
        h1: 'Wildlife conservation drawings: tiger, birds in a nest, elephant family',
        tagline: 'Three scenes, each in pencil sketch, colour pencil and outline to colour.',
        intro: 'Wildlife conservation drawings for a poster, a school competition or a chart. The set shows a tiger walking through tall grass beside a stream, a nest with three eggs and a mother bird on a branch, and an elephant mother and calf walking under trees. Each comes in three styles: pencil, colour pencil and outline to colour. Type a change in the direction field, such as a rhino, a peacock or a waterhole.',
        useCases: [
            'a save wildlife poster for a competition',
            'a school chart on animals and their homes',
            'a classroom display on the forest',
            'a colouring page for a class',
        ],
        faq: [
            {q: 'Can I print these for a school competition?', a: 'Yes. Each picture is a plain drawing on a white background, so it prints on ordinary paper.'},
            {q: 'What is the difference between the three styles?', a: 'Pencil sketch is grey shading with no colour. Colour pencil is bright crayon colour. Outline to colour is a bold black line drawing with flat colour inside each shape, easy to fill in.'},
            {q: 'Are these real animals?', a: 'No. They are drawn scenes, not photographs, each animal drawn in a simple school style.'},
            {q: 'Can I ask for a different picture?', a: 'Yes. Type the change in the direction field. It is a request, so the result may differ from the nine shown.'},
        ],
    },
}
